import { memo, useMemo } from 'react';
import { planEtiquettes } from '../../../utils/repereEtiquettes';
import {
    cadreRepere, decorSegments, segmentsRelies, flechePx, directionsDecor, directionsPolygone, etiquetteDirigee,
} from '../../../utils/repereDecor';

/**
 * Rendu STATIQUE d'un repère orthogonal avec points placés (lecture seule).
 * Utilisé par : préview prof (points de la bonne réponse en vert), correction
 * élève (points de l'élève en vert/rouge via `points` explicites).
 *
 * @param {Object} repere - Config { xMin?, yMin?, xMax, yMax, xStep, yStep, xLabel, yLabel, grille } (xMin/yMin ≤ 0 : 4 quadrants)
 * @param {string} bonneReponse - JSON string { "A": "2;8" } → points verts (si `points` absent)
 * @param {Array<{x,y,label,color}>} points - Points explicites (prioritaire sur bonneReponse)
 * @param {Array<{x,y,label}>} dejaPlaces - Points statiques gris (énoncé)
 * @param {boolean} light - Thème clair
 * @param {string} titre - Libellé au-dessus de la figure (null pour aucun)
 * @param {Object} decor - contenu.decor : figures et flèches entre points de dejaPlaces
 * @param {string[]} relier - contenu.relier : les points affichés reliés (la figure attendue)
 * repere.quadrillage : carreaux carrés, sans axes (utils/repereDecor.js, 2026-09-24).
 */

const parseXY = (str) => {
    const parts = String(str).split(';');
    if (parts.length !== 2) return null;
    const x = parseFloat(parts[0].replace(',', '.'));
    const y = parseFloat(parts[1].replace(',', '.'));
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
};

const fmtFr = (val) => String(Math.round(val * 1000) / 1000).replace('.', ',');

const Repere2DRenderer = memo(function Repere2DRenderer({
    repere, bonneReponse, points = null, dejaPlaces = [], light = false, titre = 'Points à placer',
    decor = null, relier = null,
}) {
    const {
        xMin = 0, yMin = 0, xMax = 10, yMax = 10, xStep = 1, yStep = 1,
        xLabel = '', yLabel = '', grille = true,
    } = repere || {};

    // Points à afficher : explicites, sinon dérivés de la bonne réponse (verts)
    const pts = useMemo(() => {
        if (Array.isArray(points)) return points;
        try {
            const obj = JSON.parse(bonneReponse || '{}');
            return Object.entries(obj)
                .map(([label, v]) => {
                    const xy = parseXY(v);
                    return xy ? { ...xy, label, color: '#22c55e' } : null;
                })
                .filter(Boolean);
        } catch { return []; }
    }, [points, bonneReponse]);

    // Garde-fou : config manquante/malformée → placeholder propre
    if (!repere || !(xMax > xMin) || !(yMax > yMin) || !(xStep > 0) || !(yStep > 0)) {
        return <p className={`text-xs italic ${light ? 'text-gray-400' : 'text-slate-400'}`}>Repère indisponible</p>;
    }

    // ─── Géométrie (mêmes conventions que Repere2DInteractiveEleve, utils/repereDecor) ───
    const { W, H, padL, padT, plotW, plotH, quadrillage: quad } = cadreRepere(repere);
    const toPx = (x, y) => ({
        px: padL + ((x - xMin) / (xMax - xMin)) * plotW,
        py: padT + plotH - ((y - yMin) / (yMax - yMin)) * plotH,
    });

    const makeGrads = (min, max, step) => {
        const grads = [];
        for (let v = min; v <= max + step / 2 && grads.length <= 200; v += step) {
            grads.push(Math.round(v * 1000) / 1000);
        }
        return grads;
    };
    const xGrads = makeGrads(xMin, xMax, xStep);
    const yGrads = makeGrads(yMin, yMax, yStep);
    const xEtiq = planEtiquettes(xGrads, xStep, xMin, xMax, plotW / (xMax - xMin), 22);
    const yEtiq = planEtiquettes(yGrads, yStep, yMin, yMax, plotH / (yMax - yMin), 14);

    const lineColor = light ? '#475569' : '#94a3b8';
    const gridColor = light ? '#e2e8f0' : '#334155';
    const textColor = light ? '#1e293b' : '#cbd5e1';
    const origin = toPx(0, 0);
    const S = { r: 5, txt: 10, lbl: 11, axis: 11 };
    // Étiquette d'un point : à DROITE du point (même règle que le repère interactif), sauf sur
    // l'axe horizontal où elle reste au-dessus ; ainsi un point sur l'axe vertical n'a plus son
    // nom posé sur le trait de l'axe (demande prof 2026-09-11).
    // Un point du décor ou un sommet de la figure attendue : son nom vers l'extérieur (2026-09-24)
    const dirsDecor = directionsDecor(decor, dejaPlaces);
    const dirsRelier = directionsPolygone(relier, Object.fromEntries(pts.filter((p) => p.label != null).map((p) => [p.label, p])));
    const etiquette = (px, py, p, fill, d = null) => (d ? (() => {
        const e = etiquetteDirigee(px, py, d, S.r + 8, S.lbl);
        return <text x={e.x} y={e.y} textAnchor={e.ancre} fill={fill} style={{ fontSize: S.lbl }} className="font-bold">{p.label}</text>;
    })() : p.y === 0 ? (
        <text x={px} y={py - S.r - 5} textAnchor="middle" fill={fill} style={{ fontSize: S.lbl }} className="font-bold">{p.label}</text>
    ) : (
        <text x={px + S.r + 4} y={py + S.lbl * 0.35} textAnchor="start" fill={fill} style={{ fontSize: S.lbl }} className="font-bold">{p.label}</text>
    ));

    return (
        <div>
            {titre && (
                <span className={`text-[10px] font-semibold uppercase tracking-wider mb-2 block ${light ? 'text-gray-400' : 'text-slate-400'}`}>
                    {titre}
                </span>
            )}
            <div className={`w-full rounded-xl border ${light ? 'bg-white border-slate-300' : 'border-slate-600'}`}>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full select-none" preserveAspectRatio="xMidYMid meet">
                    {/* Quadrillage (la ligne 0 est portée par les axes, sauf en quadrillage sans axes) */}
                    {grille && xGrads.map((v, i) => (quad || v !== 0) && (
                        <line key={`gx-${i}`} x1={toPx(v, 0).px} y1={padT} x2={toPx(v, 0).px} y2={padT + plotH} stroke={gridColor} strokeWidth={0.75} />
                    ))}
                    {grille && yGrads.map((v, i) => (quad || v !== 0) && (
                        <line key={`gy-${i}`} x1={padL} y1={toPx(0, v).py} x2={padL + plotW} y2={toPx(0, v).py} stroke={gridColor} strokeWidth={0.75} />
                    ))}

                    {/* Axes + flèches (toujours par l'origine — xMin/yMin ≤ 0) ; aucun en quadrillage */}
                    {quad ? null : (<>
                    <line x1={padL} y1={origin.py} x2={padL + plotW + 12} y2={origin.py} stroke={lineColor} strokeWidth={1.5} />
                    <polygon points={`${padL + plotW + 18},${origin.py} ${padL + plotW + 9},${origin.py - 4} ${padL + plotW + 9},${origin.py + 4}`} fill={lineColor} />
                    <line x1={origin.px} y1={padT + plotH} x2={origin.px} y2={padT - 12} stroke={lineColor} strokeWidth={1.5} />
                    <polygon points={`${origin.px},${padT - 18} ${origin.px - 4},${padT - 9} ${origin.px + 4},${padT - 9}`} fill={lineColor} />

                    {/* Graduations + valeurs (le 0 décalé à gauche de l'axe des ordonnées) */}
                    {xGrads.map((v, i) => {
                        const { px } = toPx(v, 0);
                        const estOrigine = v === 0 && yMin < 0;
                        return (
                            <g key={`tx-${i}`}>
                                <line x1={px} y1={origin.py - 4} x2={px} y2={origin.py + 4} stroke={lineColor} strokeWidth={1} />
                                {xEtiq[i] > 0 && (
                                    <text
                                        x={estOrigine ? px - 6 : px}
                                        y={origin.py + S.txt + 8}
                                        textAnchor={estOrigine ? 'end' : 'middle'}
                                        fill={textColor} style={{ fontSize: S.txt * xEtiq[i] }}
                                    >
                                        {fmtFr(v)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                    {yGrads.map((v, i) => {
                        if (v === 0) return null;
                        const { py } = toPx(0, v);
                        return (
                            <g key={`ty-${i}`}>
                                <line x1={origin.px - 4} y1={py} x2={origin.px + 4} y2={py} stroke={lineColor} strokeWidth={1} />
                                {yEtiq[i] > 0 && (
                                    <text x={origin.px - 8} y={py + S.txt / 3} textAnchor="end" fill={textColor} style={{ fontSize: S.txt * yEtiq[i] }}>
                                        {fmtFr(v)}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Noms des axes */}
                    {xLabel && (
                        <text x={padL + plotW + 16} y={origin.py + S.axis + 16} textAnchor="end" fill={textColor} style={{ fontSize: S.axis }} className="font-medium">
                            {xLabel}
                        </text>
                    )}
                    {yLabel && (
                        <text x={origin.px + 8} y={padT - 10} textAnchor="start" fill={textColor} style={{ fontSize: S.axis }} className="font-medium">
                            {yLabel}
                        </text>
                    )}
                    </>)}

                    {/* Décor : la figure donnée et la flèche ; puis la figure attendue, points reliés */}
                    {(() => {
                        const { cotes, fleches } = decorSegments(decor, dejaPlaces);
                        const places = {};
                        pts.forEach((p) => { if (p.label != null) places[p.label] = p; });
                        const relies = segmentsRelies(relier, places);
                        if (!cotes.length && !fleches.length && !relies.length) return null;
                        const ligne = (c, k, props) => {
                            const a = toPx(c.x1, c.y1); const b = toPx(c.x2, c.y2);
                            return <line key={k} x1={a.px} y1={a.py} x2={b.px} y2={b.py} strokeLinecap="round" {...props} />;
                        };
                        return (
                            <g data-testid="decor">
                                {cotes.map((c, i) => ligne(c, `dc-${i}`, { stroke: light ? '#334155' : '#cbd5e1', strokeWidth: 2 }))}
                                {fleches.map((f, i) => {
                                    const a = toPx(f.x1, f.y1); const b = toPx(f.x2, f.y2);
                                    const fl = flechePx(a.px, a.py, b.px, b.py, { retrait: S.r });
                                    return (
                                        <g key={`df-${i}`}>
                                            <line x1={fl.trait.x1} y1={fl.trait.y1} x2={fl.trait.x2} y2={fl.trait.y2} stroke="#16a34a" strokeWidth={2.5} strokeLinecap="round" />
                                            <polygon points={fl.pointe} fill="#16a34a" />
                                        </g>
                                    );
                                })}
                                {relies.map((c, i) => ligne(c, `rl-${i}`, { stroke: '#22c55e', strokeWidth: 2 }))}
                            </g>
                        );
                    })()}

                    {/* Points statiques de l'énoncé */}
                    {dejaPlaces.map((p, i) => {
                        const { px, py } = toPx(p.x, p.y);
                        const gray = light ? '#64748b' : '#94a3b8';
                        return (
                            <g key={`static-${i}`}>
                                <circle cx={px} cy={py} r={S.r - 1} fill={gray} stroke="white" strokeWidth={1} />
                                {p.label && etiquette(px, py, p, gray, dirsDecor[p.label])}
                            </g>
                        );
                    })}

                    {/* Points affichés (bonne réponse ou réponse élève colorée) */}
                    {pts.map((p, i) => {
                        const { px, py } = toPx(p.x, p.y);
                        const color = p.color || '#22c55e';
                        return (
                            <g key={`pt-${i}`}>
                                {quad ? null : (<>
                                    <line x1={px} y1={py} x2={px} y2={origin.py} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
                                    <line x1={px} y1={py} x2={origin.px} y2={py} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
                                </>)}
                                <circle cx={px} cy={py} r={S.r} fill={color} stroke="white" strokeWidth={1.5} />
                                {p.label && etiquette(px, py, p, color, dirsRelier[p.label])}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
});

export default Repere2DRenderer;
